import React, { useState, useEffect } from "react";
import { logException } from "@wss/error-tracking";
import { toError } from "@wss/error-tracking/utils";
import { Throwable } from "@wss/error-tracking/throwable";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import { User } from "~/common/Store/models/User";
import AddToCart from "./Pricing/AddToCart";
import Ribbons from "./Pricing/Ribbons";
import { AutoReorder, UserProductPurchasingData } from "./ProductDetailPageResult";
import Pricing from "./Pricing";
import AddToCartSuccess from "./Pricing/AddToCart/AddToCartSuccess";
import OutletOffer from "./Pricing/OutletOffer";
import OutOfStock from "./Pricing/OutOfStock";
import PriceIncreaseAdvertisement from "./Pricing/PriceIncreaseAdvertisement";
import UserProductPurchasingHistoryBanners from "./Pricing/UserProductPurchasingHistoryBanners";
import AutoReorderToggle from "./Pricing/AddToCart/AutoReorder";
import RewardsCardAd from "./Pricing/RewardsCardAd";
import ValidationError from "./Pricing/AddToCart/ValidationError";
import PlatinumPlusExclusiveBanner from "./Pricing/PlatinumPlusExclusiveBanner";
import UserLists from "./UserLists";
import ReplacementButton from "./Pricing/AddToCart/ReplacementButton";
import isGroupMemberSelected from "./Pricing/isGroupMemberSelected";
import { PricingProduct } from "./Pricing/PricingProduct";
import canCustomerPurchase from "./canCustomerPurchase";
import VirtualGrouping from "./VirtualGrouping";
import { useProductDetailPageData } from "~/ProductDetails";
import { VariationIds } from "~/common/globals";
import getABFeatureFlag from "~/common/Featbit/getABFeatureFlag";
import updateVariationIds from "~/Gtm/updateVariationIds";
import { getResponseErrorMessage, shouldLogError } from "~/common/helpers/getProductPurchasingErrorHandling";
interface Props {
  product: PricingProduct;
  quantity: string;
  hasQuantitySelect: boolean;
  onQuantityChanged: (value: string) => void;
  user: User;
  handleVirtualGroupingSelectChange: (itemNumber: string) => string;
  setAddToCartSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  allowOutOfStockPurchase: boolean;
  mounted: boolean;
  minimalPageVersion?: boolean;
}
const ProductDetailPricingBox = connectUserAndChat(({
  product,
  quantity,
  hasQuantitySelect,
  onQuantityChanged,
  user,
  handleVirtualGroupingSelectChange,
  setAddToCartSuccess,
  allowOutOfStockPurchase,
  mounted,
  minimalPageVersion
}: Props) => {
  const [totalNumOfItemsAddedToCart, setTotalNumOfItemsAddedToCart] = useState(0);
  const {
    isGoogleView,
    productDetailGroupingSelect
  } = useProductDetailPageData();
  const [userProductPurchasingData, setUserProductPurchasingData] = useState<UserProductPurchasingData>({
    activeAutoReorderId: 0,
    nextAutoReorderDateFormatted: "",
    hasMultipleAutoReorders: false,
    previouslyPurchasedOrderNumber: 0,
    previouslyPurchasedDateFormatted: "",
    activeAutoReorders: []
  });
  const [autoReorder, setAutoReorder] = useState<AutoReorder>({
    isToggleEnabled: false,
    isAddToExistingAutoReorder: false,
    newAutoReorderInterval: "0",
    selectedExistingAutoReorder: {
      name: "",
      isActive: false,
      creditCardId: 0,
      desiredOrderDate: "",
      id: 0
    }
  });
  const [addToCartError, setAddToCartError] = useState<ValidationError>();
  const [errorMessage, setErrorMessage] = useState("");
  const handleAutoReorderChanged = (autoReorderData: AutoReorder) => {
    setAutoReorder(autoReorderData);
  };
  const handleAddToCartQuantityChange = (quantity: string) => {
    onQuantityChanged(quantity);
  };
  const handleVgSelectionError = (validationError: ValidationError) => {
    handleAddToCartError(validationError);
    handleSetErrorMessage(validationError.message);
  };
  const handleSetErrorMessage = (errorMessage: string) => {
    setErrorMessage(errorMessage);
  };
  const handleAddToCartError = (validationError: ValidationError | undefined) => {
    setAddToCartError(validationError);
  };
  const {
    outletOffer,
    price,
    isInStock,
    itemNumber,
    priceIncreaseAdvertisement,
    isAutomaticReorderEligible,
    customerExclusivity,
    isReplacementEligible,
    productReplacement,
    customizationRequired,
    isCustomizable,
    customizationUrl
  } = product;

  // google bots won't be logged in so we will display as normal when user is logged in
  const showGoogleView = isGoogleView && !user.registered;
  const hasSelectedGroupMember = isGroupMemberSelected(product);
  const canCustomerModifyAutoReorder = !(user.supervisorIndex !== "0" && user.requiresSupervisorApproval);
  const shouldDisplayRequiredCustomization = Boolean(customizationRequired && (isCustomizable || !!customizationUrl));
  useEffect(() => {
    async function getProductPurchasingHistory(itemNumbers: string[]): Promise<Throwable> {
      try {
        const response = await fetch("/api/productdetails/getproductpurchasingactivity", {
          method: "POST",
          headers: {
            "Content-type": "application/json"
          },
          body: JSON.stringify({
            itemNumbers
          })
        });
        if (!response.ok) {
          const error = new Error(getResponseErrorMessage(response));

          // Don't log 500 errors since should be logged on the server
          if (!shouldLogError(response)) {
            return {
              error,
              isError: true
            };
          }
          throw error;
        }
        setUserProductPurchasingData(await response.json<UserProductPurchasingData>());
        return {
          isError: false
        };
      } catch (e) {
        const error = new Error("Error in getProductPurchasingHistory", {
          cause: toError(e)
        });
        await logException(error);
        setUserProductPurchasingData({
          activeAutoReorderId: 0,
          nextAutoReorderDateFormatted: "",
          hasMultipleAutoReorders: false,
          previouslyPurchasedOrderNumber: 0,
          previouslyPurchasedDateFormatted: "",
          activeAutoReorders: []
        });
        return {
          error,
          isError: true
        };
      }
    }
    if (user.registered && !shouldDisplayRequiredCustomization) {
      const itemNumbers = product.groupingAttributes?.isGroupMaster ? productDetailGroupingSelect.map(option => option.itemNumber) : [product.itemNumber];
      getProductPurchasingHistory(itemNumbers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.itemNumber, user.registered]);
  const [quantityDiscountV2IsEnabled, setQuantityDiscountV2IsEnabled] = useState(false);
  useEffect(() => {
    const setABVariationData = async (sessionId: string, emailAddress?: string) => {
      const result = await getABFeatureFlag({
        featureFlagName: "pdp-quantity-discounts-design-ab-test",
        defaultValue: "A",
        customProps: [{
          key: "Page",
          value: window.location.pathname
        }]
      }, {
        sessionId,
        emailAddress
      });
      if (result.isError) {
        return;
      }
      setQuantityDiscountV2IsEnabled(result.value.stringValue === "B");
      if (result.value.stringValue !== "C") {
        updateVariationIds([{
          featureName: VariationIds.Quantity_Discount_AB_Test,
          featureOption: result.value.stringValue
        }]);
      }
    };
    if (user.sessionId) {
      setABVariationData(user.sessionId, user.emailAddress);
    }
  }, [quantityDiscountV2IsEnabled, user.emailAddress, user.sessionId]);
  return <div id="subject" className="side-col ag-product mb-4">
        <UserProductPurchasingHistoryBanners userProductPurchasingData={userProductPurchasingData} />
        <div className={`ag-item box${customerExclusivity?.disallowNonMemberToPurchase ? " member-exclusive" : ""}`} id="priceBox" style={{
      scrollMarginTop: "60px"
    }}>
          {customerExclusivity?.disallowNonMemberToPurchase && <PlatinumPlusExclusiveBanner isPlusMember={!!user.hasWebPlus} isPlatinumMember={user.platinum?.enabled} />}
          <Ribbons product={product} user={user} showGoogleView={showGoogleView} hideQuantityDiscount={quantityDiscountV2IsEnabled} />
          <Pricing product={product} user={user} isGoogleView={showGoogleView} quantityDiscountV2IsEnabled={quantityDiscountV2IsEnabled} />
          {outletOffer && <OutletOffer price={outletOffer.price} link={outletOffer.link} hasMapStyle={!!price.minimumAdvertisedPriceProperties?.type.trim()} />}
          {mounted && productDetailGroupingSelect.length ? <VirtualGrouping productGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} virtualGroupMemberToDisplay={product.groupingAttributes.isGroupMaster ? "" : product.itemNumber} addToCartError={addToCartError} handleAddToCartError={handleAddToCartError} user={user} /> : null}
          {priceIncreaseAdvertisement && <PriceIncreaseAdvertisement priceIncreaseAdvertisement={priceIncreaseAdvertisement} />}
          {isAutomaticReorderEligible && canCustomerModifyAutoReorder && <AutoReorderToggle itemNumber={product.itemNumber} hasFreeShipping={product.hasFreeShipping} isWebstaurantPlusEligible={product.isWebstaurantPlusEligible} user={user} userProductPurchasingData={userProductPurchasingData} autoReorder={autoReorder} onChange={handleAutoReorderChanged} isInStock={isInStock} mounted={mounted} />}
          {isInStock && canCustomerPurchase(customerExclusivity, user) && <AddToCart product={product} user={user} totalNumOfItemsAddedToCart={totalNumOfItemsAddedToCart} setTotalNumOfItemsAddedToCart={setTotalNumOfItemsAddedToCart} handleAddToCartError={handleAddToCartError} isGoogleView={showGoogleView} autoReorder={autoReorder} setAddToCartSuccess={setAddToCartSuccess} handleSetErrorMessage={handleSetErrorMessage} quantity={quantity} hasQuantitySelect={hasQuantitySelect} handleAddToCartQuantityChange={handleAddToCartQuantityChange} previouslyPurchasedOrderNumber={userProductPurchasingData.previouslyPurchasedOrderNumber} />}
          {!isInStock && allowOutOfStockPurchase && canCustomerPurchase(customerExclusivity, user) && <>
                <AddToCart product={product} user={user} totalNumOfItemsAddedToCart={totalNumOfItemsAddedToCart} setTotalNumOfItemsAddedToCart={setTotalNumOfItemsAddedToCart} handleAddToCartError={handleAddToCartError} isGoogleView={showGoogleView} autoReorder={autoReorder} setAddToCartSuccess={setAddToCartSuccess} handleSetErrorMessage={handleSetErrorMessage} quantity={quantity} hasQuantitySelect={hasQuantitySelect} handleAddToCartQuantityChange={handleAddToCartQuantityChange} previouslyPurchasedOrderNumber={userProductPurchasingData.previouslyPurchasedOrderNumber} />
                {!product.customizationRequired && <OutOfStock itemNumber={itemNumber} emailAddress={user.emailAddress || ""} itemSelected={hasSelectedGroupMember} />}
              </>}
          {!isInStock && !allowOutOfStockPurchase && canCustomerPurchase(customerExclusivity, user) && (isReplacementEligible ? <ReplacementButton replacementLink={productReplacement.replacementLink} /> : <OutOfStock itemNumber={itemNumber} emailAddress={user.emailAddress || ""} itemSelected={hasSelectedGroupMember} />)}
          <div className="add-to-cart" aria-live="assertive">
            <p className="messages" id="errorMessageId" aria-live="polite" aria-atomic="true">
              {errorMessage}
            </p>
          </div>
          <RewardsCardAd product={product} user={user} />
        </div>
        {totalNumOfItemsAddedToCart > 0 && <AddToCartSuccess quantity={totalNumOfItemsAddedToCart} />}
        {!minimalPageVersion && <UserLists user={user} handleVgSelectionError={handleVgSelectionError} product={product} addToCartQuantity={quantity} />}
      </div>;
});
export default ProductDetailPricingBox;